import {Injectable} from '@angular/core';
import {OffsetDto} from "../../models/vector-api/offset-dto.model";
import {IBatchForecastMeasurement} from "../../models/batch-forecast-measurement.model";
import {ChartFilters} from "../../models/chart-filters.model";
import {IMwgData} from "../../models/chart-mwg.model";
import {VectorHistoryService} from "./vector-history.service";

@Injectable({
  providedIn: 'root'
})
export class OffsetHistoryService {

  // prefix for offset history in storage
  public static OFFSET_STORAGE_PREFIX: string = 'OFFSET-B_';
  // prefix for carb history in storage
  public static CARB_STORAGE_PREFIX: string = 'CARB-B_';

  // possible mwg filter indicators
  private TOL_ON_LAB: string = 'TolOnLab';
  private TOL_OFF_LAB: string = 'TolOffLab';
  private TOL_ON_LCH: string = 'TolOnLch';
  private TOL_OFF_LCH: string = 'TolOffLch';

  // storage for storing offset history -> session or local storage can be used session storage will be preferred
  private storage: Storage;

  constructor() {
    // switch between session or localstorage
    // this.storage = localStorage;
    this.storage = sessionStorage;
  }

  public addToHistory(refBatch: any, refMeasurement: IBatchForecastMeasurement, chartFilters: ChartFilters, offset: OffsetDto, theory: IMwgData, selectedTintingTarget: string): void {
    const identifier = this.getIdentifier(refBatch, refMeasurement, theory, selectedTintingTarget);

    const offSetFromStorage: OffsetDto = this.getFromStorage(identifier);
    if (offSetFromStorage) {
      if (!offSetFromStorage.MwgDatas) {
        // init empty
        offSetFromStorage.MwgDatas = {};
      }
      // if we already have an offset for the current identifier updated the corresponding saved offset in the storage
      // BCH-449850 Bugfix: determine storage indicator from data itself because chartFilters might not be updated on the initial load when the backend determines tols on/off
      // offSetFromStorage.MwgDatas[this.getIndicatorMwgFilter(chartFilters)] = offset.AvgView;
      offSetFromStorage.MwgDatas[this.getIndicatorMwgFilter({labSystem: offset.AvgView.colorSpace, tolerance: offset.AvgView.tolerances})] = offset.AvgView;
      this.saveToStorage(identifier, offSetFromStorage);
      return;
    }

    // save the offset to storage
    if (!offset.MwgDatas) {
      // init empty
      offset.MwgDatas = {};
    }
    // BCH-449850 Bugfix: determine storage indicator from data itself because chartFilters might not be updated on the initial load when the backend determines tols on/off
    // offset.MwgDatas[this.getIndicatorMwgFilter(chartFilters)] = offset.AvgView;
    offset.MwgDatas[this.getIndicatorMwgFilter({labSystem: offset.AvgView.colorSpace, tolerance: offset.AvgView.tolerances})] = offset.AvgView;
    this.saveToStorage(identifier, offset);
  }

  public getFromHistory(refBatch: any, refMeasurement: IBatchForecastMeasurement, chartFilters: ChartFilters, theory: IMwgData, selectedTintingTarget: string): OffsetDto {
    // get Offset from storage for the current identifier
    const identifier = this.getIdentifier(refBatch, refMeasurement, theory, selectedTintingTarget);
    const offsetFromStorage: OffsetDto = this.getFromStorage(identifier);
    if (!offsetFromStorage) {
      return null;
    }

    // check if offset has the mwg data for the current filters if not set to null so it gets loaded from the server
    const requestedMwg: IMwgData = offsetFromStorage.MwgDatas[this.getIndicatorMwgFilter(chartFilters)];
    if (!requestedMwg) {
      offsetFromStorage.AvgView = null;
    } else {
      offsetFromStorage.AvgView = requestedMwg;
    }

    return offsetFromStorage;
  }

  private getIdentifier(refBatch: any, refMeasurement: IBatchForecastMeasurement, theory: IMwgData, selectedTintingTarget: string): string {
    let identifier = ((selectedTintingTarget === VectorHistoryService.TINTING_TARGET_BATCH || selectedTintingTarget === VectorHistoryService.TINTING_TARGET_RS) ? OffsetHistoryService.CARB_STORAGE_PREFIX : OffsetHistoryService.OFFSET_STORAGE_PREFIX) + refBatch.id;
    if (refMeasurement) {
      identifier += '-MQC_' + refMeasurement.id;
    }
    if (theory) {
      identifier += '-T_' + theory.table.name;
    }
    return identifier;
  }

  private saveToStorage(identifier: string, offset: OffsetDto): void {
    this.storage.setItem(identifier, JSON.stringify(offset));
  }

  private getFromStorage(identifier: string): OffsetDto {
    return JSON.parse(this.storage.getItem(identifier));
  }

  private getIndicatorMwgFilter(chartFilters: ChartFilters): string {
    const isLab = chartFilters.labSystem === 'LAB';
    const tols = chartFilters.tolerance;
    if (tols && isLab) {
      return this.TOL_ON_LAB;
    } else if (tols && !isLab) {
      return this.TOL_ON_LCH;
    } else if (!tols && isLab) {
      return this.TOL_OFF_LAB;
    } else {
      return this.TOL_OFF_LCH;
    }
  }

  /*
  // currently deleting is done via vector history service
  private deleteFromStorage(identifier: string): void {
    this.storage.removeItem(identifier);
  }*/
}
